/**
 * ParentBridge — postMessage contract between this game (running in an iframe)
 * and the host parent page.
 *
 * Protocol (both directions use the same message shape):
 *   { type: 'SET_MUTE',  payload: boolean }   // true = muted
 *   { type: 'SET_PAUSE', payload: boolean }   // true = paused
 *
 * Inbound (parent → game): the parent commands a state. The bridge applies it
 * to the game (mute via SoundManager, pause via the registered GameScene).
 *
 * Outbound (game → parent): whenever mute or pause actually CHANGES — from any
 * source (a parent command, an in-game button, a keyboard shortcut, or the
 * auto-pause on tab hide) — the bridge posts the same message back as a
 * confirmation of the new state.
 *
 * Loop avoidance: confirmations are only sent on a real state transition.
 * Applying a parent command that matches the current state is a no-op and
 * produces no echo, so a parent that re-sends on receiving a confirmation
 * won't create a feedback loop. Even so, parents should treat inbound
 * confirmations as informational and not echo them back.
 *
 * Framing safety: messages are only posted when actually embedded
 * (window.parent !== window). All postMessage / handler calls are guarded so a
 * hostile or malformed message can't throw into the game loop.
 */

export type BridgeMessageType = 'SET_MUTE' | 'SET_PAUSE';

export interface BridgeMessage {
  type: BridgeMessageType;
  payload: boolean;
}

/** Something that can be paused/resumed on command (the active GameScene). */
export interface Pausable {
  /** Apply a desired paused state. Implementations must be idempotent. */
  applyPauseFromParent(paused: boolean): void;
  /** Current paused state, for echo decisions. */
  isPaused(): boolean;
}

/** Something that can be muted on command (SoundManager). */
export interface Mutable {
  /** Apply a desired muted state. Implementations must be idempotent. */
  applyMuteFromParent(muted: boolean): void;
  /** Current muted state, for echo decisions. */
  isMuted(): boolean;
}

class _ParentBridge {
  private started = false;
  private mutable?: Mutable;
  private pausable?: Pausable;
  /** Guards against re-emitting while we're applying a parent command. */
  private applying = false;
  /** Optional UI listeners notified whenever mute state changes (any source). */
  private muteListeners = new Set<(muted: boolean) => void>();

  /** Begin listening for parent messages. Safe to call once at boot. */
  start(): void {
    if (this.started) return;
    this.started = true;
    if (typeof window === 'undefined') return;
    window.addEventListener('message', this.onMessage);
  }

  /** Register the global mute controller (SoundManager). */
  registerMutable(m: Mutable): void {
    this.mutable = m;
  }

  /**
   * Subscribe to mute-state changes for UI sync (e.g. the menu's speaker
   * icon). Returns an unsubscribe function. Fired on every real change
   * regardless of source: parent command, in-game toggle, etc.
   */
  onMuteChange(fn: (muted: boolean) => void): () => void {
    this.muteListeners.add(fn);
    return () => this.muteListeners.delete(fn);
  }

  /**
   * Register the active pause controller. The current GameScene calls this on
   * create() and clears it (passing null) on shutdown so we never hold a
   * reference to a torn-down scene.
   */
  registerPausable(p: Pausable | null): void {
    this.pausable = p ?? undefined;
  }

  // --- Outbound confirmations ------------------------------------------

  /**
   * Notify the parent that mute changed. Called by SoundManager after a real
   * change. Suppressed while we're applying a parent command (the parent
   * already knows — it asked).
   */
  notifyMuteChanged(muted: boolean): void {
    // UI listeners fire on every real change, even parent-originated ones, so
    // in-game labels stay in sync with parent commands.
    for (const fn of this.muteListeners) {
      try {
        fn(muted);
      } catch (err) {
        console.warn('splat-chimp: mute listener threw', err);
      }
    }
    // The postMessage confirmation is suppressed while applying a parent
    // command (the parent already knows — it asked).
    if (this.applying) return;
    this.post({ type: 'SET_MUTE', payload: muted });
  }

  /** Notify the parent that pause changed. Called by GameScene. */
  notifyPauseChanged(paused: boolean): void {
    if (this.applying) return;
    this.post({ type: 'SET_PAUSE', payload: paused });
  }

  // --- Internals --------------------------------------------------------

  private onMessage = (ev: MessageEvent): void => {
    const msg = this.parse(ev.data);
    if (!msg) return;
    this.applying = true;
    try {
      if (msg.type === 'SET_MUTE') {
        // payload true = muted. Idempotent inside the controller.
        this.mutable?.applyMuteFromParent(msg.payload);
      } else if (msg.type === 'SET_PAUSE') {
        this.pausable?.applyPauseFromParent(msg.payload);
      }
    } catch (err) {
      console.warn('splat-chimp: error applying parent message', err);
    } finally {
      this.applying = false;
    }
  };

  /** Validate an inbound message into a typed BridgeMessage, or null. */
  private parse(data: unknown): BridgeMessage | null {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    if (d.type !== 'SET_MUTE' && d.type !== 'SET_PAUSE') return null;
    if (typeof d.payload !== 'boolean') return null;
    return { type: d.type, payload: d.payload };
  }

  private post(msg: BridgeMessage): void {
    try {
      if (typeof window === 'undefined') return;
      // Only meaningful when embedded.
      if (window.parent === window) return;
      // Target origin '*' — the host controls which origins it embeds; if you
      // want to lock this down, replace with the known parent origin.
      window.parent.postMessage(msg, '*');
    } catch (err) {
      console.warn('splat-chimp: failed to post message to parent', err);
    }
  }
}

export const ParentBridge = new _ParentBridge();
